<script setup lang="ts">
/**
 * Signing in.
 *
 * Pick a name, type a PIN. Not a username field — a server holding plates is
 * not typing an email address, and the question this answers is only "which of
 * the eight people on this shift is holding the handheld". The device has
 * already proved itself to the platform with a key; the PIN is a convenience
 * credential over an already-authenticated device.
 *
 * NAMES ARE SHOWN. That is deliberate and it is not a leak: the roster is who
 * is on shift in this room, and everyone can already see each other. Making
 * staff type an identifier as well would buy nothing against the real threat —
 * a colleague reaching for someone else's account — and cost a tap every time.
 *
 * The KEYPAD is on screen rather than relying on the OS keyboard, which on a
 * kiosked Android tablet is often not there at all.
 */
import { computed, ref } from "vue";
import type { Translator } from "@gf/kernel/i18n";

export interface LoginCandidate {
  readonly staffId: string;
  readonly displayName: string;
  readonly roleId: string;
}

const props = defineProps<{
  staff: readonly LoginCandidate[];
  t: Translator;
  /** A message key, when the last attempt failed. */
  error?: string | null;
  /** Seconds remaining while this staff member is locked out, or 0. */
  lockedForSeconds?: number;
  busy?: boolean;
}>();

const emit = defineEmits<{ signIn: [{ staffId: string; pin: string }] }>();

const selected = ref<string | null>(null);
const pin = ref("");

const MIN_PIN = 6;

const chosen = computed(() => props.staff.find((s) => s.staffId === selected.value) ?? null);
const locked = computed(() => (props.lockedForSeconds ?? 0) > 0);
const canSubmit = computed(
  () => chosen.value !== null && pin.value.length >= MIN_PIN && !locked.value && !props.busy,
);

const press = (digit: string): void => {
  // Bounded so a stuck key cannot build a megabyte-long string that the
  // key-derivation then spends real time on.
  if (pin.value.length < 12) pin.value += digit;
};

const clear = (): void => {
  pin.value = "";
};

const submit = (): void => {
  if (!canSubmit.value || !chosen.value) return;
  emit("signIn", { staffId: chosen.value.staffId, pin: pin.value });
  // Cleared immediately, whatever the outcome. A PIN left in the box is a PIN
  // the next person to pick up the handheld can read off the screen.
  pin.value = "";
};

const choose = (staffId: string): void => {
  selected.value = staffId;
  pin.value = "";
};
</script>

<template>
  <section class="login" :class="{ 'login--chosen': chosen !== null }">
    <h1 class="login__title">{{ t.t("pos.login.title") }}</h1>

    <ul class="login__staff">
      <li v-for="member in staff" :key="member.staffId">
        <button
          type="button"
          class="who"
          :class="{ 'who--chosen': member.staffId === selected }"
          :aria-pressed="member.staffId === selected"
          @click="choose(member.staffId)"
        >
          <span class="who__name">{{ member.displayName }}</span>
          <span class="who__role">{{ t.t(`pos.role.${member.roleId}`) }}</span>
        </button>
      </li>
    </ul>

    <div v-if="chosen" class="login__pin">
      <p class="login__for">{{ t.t("pos.login.for", { name: chosen.displayName }) }}</p>

      <!-- The PIN itself is never rendered, only its length. A server types it
           at a bar with people on both sides. -->
      <p class="login__dots" :aria-label="t.t('pos.login.digitsEntered', { count: pin.length })">
        <span v-for="i in pin.length" :key="i" class="login__dot">●</span>
      </p>

      <p v-if="locked" class="login__error" role="alert">
        {{ t.t("pos.login.lockedOut", { seconds: lockedForSeconds ?? 0 }) }}
      </p>
      <p v-else-if="error" class="login__error" role="alert">{{ t.t(error) }}</p>

      <div class="keypad">
        <button
          v-for="digit in ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0']"
          :key="digit"
          type="button"
          class="keypad__key"
          :disabled="locked"
          @click="press(digit)"
        >
          {{ digit }}
        </button>
        <button type="button" class="keypad__key" :disabled="locked" @click="clear()">
          {{ t.t("pos.login.clear") }}
        </button>
        <button
          type="button"
          class="keypad__key keypad__key--go"
          :disabled="!canSubmit"
          @click="submit()"
        >
          {{ t.t("pos.login.signIn") }}
        </button>
      </div>
    </div>
    <!-- Where the keypad will be: on a landscape till the right-hand column
         would otherwise be an empty half of the screen asking nothing. -->
    <p v-else class="login__pick">{{ t.t("pos.login.pick") }}</p>
  </section>
</template>

<style scoped>
/*
  THE FIRST SCREEN OF EVERY SHIFT.

  It was a centred column — roster above keypad — and on a 1280 × 800 till the
  "Přihlásit" key sat below the fold: four names and four rows of keys do not
  fit 800 px, so every shift began with a scroll. On a landscape till it is now
  two columns, who on the left and the PIN on the right, both whole on the
  screen; a phone keeps one column, tightened until the key it exists for is
  visible at 390 × 844. A long roster scrolls inside its own column and never
  pushes the keypad away.
*/
.login {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-4);
  width: min(30rem, 100%);
  min-height: 0;
  margin: auto;
  padding: var(--gf-space-4);
}

.login__title {
  margin: 0;
  font-size: var(--gf-text-xl);
  font-weight: var(--gf-weight-semibold);
  line-height: var(--gf-leading-tight);
  letter-spacing: var(--gf-tracking-tight);
}

.login__staff {
  display: grid;
  align-content: start;
  gap: var(--gf-space-2);
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.who {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-2);
  width: 100%;
  min-height: var(--gf-target-min);
  padding: 0 var(--gf-space-4);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-surface);
  color: var(--gf-color-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

@media (hover: hover) {
  .who:hover {
    background: var(--gf-color-surface-hover);
  }
}

/* Chosen, without moving the list: the neutral pressed-in state with a firmer
   edge. Not the accent — that is the colour of the button that pays. */
.who--chosen {
  background: var(--gf-color-surface-active);
  box-shadow: inset 0 0 0 var(--gf-border-thin) var(--gf-color-border);
}

@media (hover: hover) {
  .who--chosen:hover {
    background: var(--gf-color-surface-active);
    box-shadow: inset 0 0 0 var(--gf-border-thin) var(--gf-color-border);
  }
}

.who:focus-visible,
.keypad__key:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

.who__name {
  font-weight: var(--gf-weight-semibold);
}

.who__role {
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
}

.login__pin {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
}

.login__for,
.login__pick {
  margin: 0;
  color: var(--gf-color-text-muted);
}

/*
  The dots. Never the PIN — `apps/pos/test/ui.spec.ts` asserts that only the
  LENGTH is ever rendered, and the `aria-label` says how many, not which.
*/
.login__dots {
  margin: 0;
  min-height: calc(var(--gf-text-2xl) * 1.2);
  font-size: var(--gf-text-2xl);
  line-height: 1.2;
  letter-spacing: 0.3em;
  color: var(--gf-color-accent);
}

.login__error {
  margin: 0;
  padding: var(--gf-space-3) var(--gf-space-4);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-danger-surface);
  color: var(--gf-color-text);
  font-weight: var(--gf-weight-medium);
}

.keypad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--gf-space-2);
}

.keypad__key {
  min-height: calc(var(--gf-target-min) * 1.15);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface);
  box-shadow: var(--gf-shadow-sm);
  color: var(--gf-color-text);
  font: inherit;
  font-size: var(--gf-text-lg);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    box-shadow var(--gf-motion-fast) var(--gf-ease-out);
}

@media (hover: hover) {
  .keypad__key:hover:not(:disabled) {
    background: var(--gf-color-surface-hover);
  }
}

/* A keypad with no press feedback is one people press twice: the key sinks
   into the page — darker, its shadow gone — without moving. */
.keypad__key:active:not(:disabled) {
  background: var(--gf-color-surface-active);
  box-shadow: none;
}

/* The one action this screen exists for. */
.keypad__key--go {
  grid-column: span 2;
  background: var(--gf-color-primary);
  border-color: var(--gf-color-primary);
  color: var(--gf-color-on-primary);
  font-weight: var(--gf-weight-semibold);
  box-shadow: var(--gf-shadow-glow);
}

@media (hover: hover) {
  .keypad__key--go:hover:not(:disabled) {
    background: color-mix(in srgb, var(--gf-color-primary) 88%, var(--gf-color-text));
  }
}

.keypad__key--go:active:not(:disabled) {
  background: color-mix(in srgb, var(--gf-color-primary) 80%, var(--gf-color-text));
}

.keypad__key:disabled {
  opacity: 0.5;
  box-shadow: none;
  cursor: not-allowed;
}

/*
 * A primary that cannot be pressed yet is NEUTRAL, not a faded red: a washed-out
 * oxblood still reads as "the button to press", and pink is not a state.
 */
.keypad__key--go:disabled {
  color: var(--gf-color-text-muted);
  background: var(--gf-color-surface-active);
  border-color: transparent;
  box-shadow: none;
  opacity: 1;
}

/* A landscape till: who on the left, the PIN on the right, both whole. */
@media (orientation: landscape) and (min-width: 48rem) {
  .login {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
    grid-template-areas:
      "title pin"
      "staff pin";
    column-gap: var(--gf-space-7);
    row-gap: var(--gf-space-4);
    width: min(56rem, 100%);
    max-height: 100%;
    padding: var(--gf-space-5);
  }

  .login__title {
    grid-area: title;
  }

  .login__staff {
    grid-area: staff;
  }

  .login__pin,
  .login__pick {
    grid-area: pin;
    align-self: center;
  }

  .who {
    min-height: calc(var(--gf-target-min) * 1.1);
  }

  .keypad__key {
    min-height: calc(var(--gf-target-min) * 1.2);
  }
}
</style>
